import { Suspense, useEffect } from 'react';

import { ChunkLoadErrorBoundary } from '@trello/error-boundaries';
import { useFeatureGate } from '@trello/feature-gate-client';
import { ApolloProvider } from '@trello/graphql';
import { TrelloIntlProvider } from '@trello/i18n';
import { Spinner } from '@trello/nachos/spinner';
import { useLocation } from '@trello/router';
import { isReactRoutingEnabled } from '@trello/router/migration';
import { UFORootSegment } from '@trello/ufo';
import { useLazyComponent } from '@trello/use-lazy-component';

import { TrelloOnline } from 'app/src/components/TrelloOnline';
import { useApolloReadWriteReporter } from './useApolloReadWriteReporter';
import { useOfflineState } from './useOfflineState';
import { useQuickload } from './useQuickload';
import { useSecurityPolicyViolationReporter } from './useSecurityPolicyViolationReporter';

export const App = () => {
  const reactRoutingEnabledOnInit = isReactRoutingEnabled();
  const {
    value: reactRoutingCurrentlyEnabled,
    loading: reactRoutingEnableGateLoading,
  } = useFeatureGate('tplat_react_router');
  const wasEverOffline = useOfflineState();
  useQuickload();
  useApolloReadWriteReporter();
  useSecurityPolicyViolationReporter();

  const { pathname } = useLocation();

  /** Migratory logic for react-router — Passively enqueue a hard page reload when the original state of the react router
   * flag changes. This hard reload should only occur on the next navigation event. This will allow us to fairly immediately
   * drop users from a react router cohort if we discover any serious issues.
   */
  useEffect(() => {
    if (
      !reactRoutingEnableGateLoading &&
      reactRoutingEnabledOnInit !== reactRoutingCurrentlyEnabled
    ) {
      location.pathname = pathname;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This hook should ONLY execute on path change, to avoid reloading a page while in use by a user
  }, [pathname]);

  if (wasEverOffline) {
    window.location.href = `https://trello.status.atlassian.com/`;
  }

  const ReactRouter = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "migration-router" */ 'app/src/components/MigrationRouter/ReactRouterWrapper'
      ),
    {
      namedImport: 'ReactRouterWrapper',
      preload: false,
    },
  );

  const getAppContent = () => {
    if (reactRoutingEnabledOnInit) {
      return (
        <Suspense fallback={<Spinner centered />}>
          <ChunkLoadErrorBoundary fallback={null}>
            <ReactRouter />
          </ChunkLoadErrorBoundary>
        </Suspense>
      );
    }
    return <TrelloOnline />;
  };

  return (
    <UFORootSegment>
      <TrelloIntlProvider>
        <ApolloProvider>{getAppContent()}</ApolloProvider>
      </TrelloIntlProvider>
    </UFORootSegment>
  );
};
